import React, { useEffect, useMemo, useRef, useState } from 'react';
import { CheckCircle2, Keyboard, MapPin, ScanLine } from 'lucide-react';
import PdaPage from '../components/PdaPage';
import {
  ScanInboundItemPayload,
  scanOffcutInbound,
  scanOffcutInboundBatch,
} from '../services/api';
import { usePersistentFocus } from '../hooks/usePersistentFocus';
import { navigateTo } from '../utils/navigation';
import {
  detectScanCodeKind,
  normalizeScannedCode,
} from '../utils/pdaCodes';
import { getPdaErrorMessage } from '../utils/pdaErrors';
import { notifyError, notifySuccess } from '../utils/pdaFeedback';
import { formatWeight } from '../utils/formatters';
import { getStoredUser } from '../utils/session';

interface PendingInboundItem extends ScanInboundItemPayload {
  id: number;
}

const AUTO_SCAN_DELAY_MS = 500;
const SOURCE_TYPE_OPTIONS = ['锯切余料', '退料', '外协退回', '其他'] as const;
const KNOWN_INBOUND_ERROR_CODES = new Set([
  '400200264001',
  '400200264007',
  '400200264010',
  '400200264013',
  '400200264014',
  '400200264015',
  '404200268005',
  'WhLocNotFound',
  '400200268006',
  'WhLocDisabled',
]);

function ScanOffcutInbound() {
  const inputRef = useRef<HTMLInputElement>(null);
  const resetTimerRef = useRef<number>(0);
  const scanTimerRef = useRef<number>(0);
  const [scanValue, setScanValue] = useState('');
  const [locCode, setLocCode] = useState('');
  const [draftOcsf, setDraftOcsf] = useState('');
  const [draftWeight, setDraftWeight] = useState('');
  const [draftLength, setDraftLength] = useState('');
  const [draftSourceType, setDraftSourceType] = useState<(typeof SOURCE_TYPE_OPTIONS)[number]>('锯切余料');
  const [draftSourceNo, setDraftSourceNo] = useState('');
  const [pendingItems, setPendingItems] = useState<PendingInboundItem[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const operator = getStoredUser();
  const operatorDisplay = operator?.full_name || operator?.username || operator?.emp_no || '员工';
  const step = !locCode ? 'location' : !draftOcsf ? 'offcut' : 'details';
  const totalPendingWeight = useMemo(
    () => pendingItems.reduce((sum, item) => sum + item.weight, 0),
    [pendingItems],
  );
  const progressSteps = [
    {
      complete: Boolean(locCode),
      current: step === 'location',
      icon: MapPin,
      label: '库位',
    },
    {
      complete: Boolean(draftOcsf),
      current: step === 'offcut',
      icon: ScanLine,
      label: '料头',
    },
    {
      complete: pendingItems.length > 0,
      current: step === 'details',
      icon: CheckCircle2,
      label: '明细',
    },
    {
      complete: Boolean(successMessage),
      current: false,
      icon: CheckCircle2,
      label: '提交',
    },
  ];

  usePersistentFocus(
    inputRef,
    Boolean(operator) && !successMessage && step !== 'details',
    `${step}:${locCode}:${draftOcsf}:${pendingItems.length}`,
  );

  useEffect(
    () => () => {
      window.clearTimeout(resetTimerRef.current);
      window.clearTimeout(scanTimerRef.current);
    },
    [],
  );

  if (!operator) {
    navigateTo('/login', true);
    return null;
  }

  function clearScanInput() {
    window.clearTimeout(scanTimerRef.current);
    setScanValue('');
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  }

  function clearCurrentDraft() {
    clearScanInput();
    setDraftOcsf('');
    setDraftWeight('');
    setDraftLength('');
    setDraftSourceType('锯切余料');
    setDraftSourceNo('');
  }

  function resetFlow() {
    clearCurrentDraft();
    setLocCode('');
    setPendingItems([]);
    setSubmitting(false);
    setSuccessMessage('');
  }

  function clearCurrentBatch() {
    clearCurrentDraft();
    setPendingItems([]);
    setSuccessMessage('');
  }

  function handleRescanLocation() {
    if ((pendingItems.length > 0 || draftOcsf) && !window.confirm('重扫库位会清空当前待提交料头，确认继续？')) {
      return;
    }

    clearCurrentDraft();
    setLocCode('');
    setPendingItems([]);
    setSuccessMessage('');
  }

  function scheduleResetAfterSubmit() {
    window.clearTimeout(resetTimerRef.current);
    resetTimerRef.current = window.setTimeout(() => {
      clearCurrentDraft();
      setPendingItems([]);
      setSubmitting(false);
      setSuccessMessage('');
    }, 2000);
  }

  function showUnsupportedCodeError(kind: string) {
    if (kind === 'equipment') {
      notifyError('设备扫码功能尚未开放');
      return;
    }
    if (kind === 'rawMaterial') {
      notifyError('v1.0 不处理原材料批次码');
      return;
    }
    notifyError('请按当前步骤扫码');
  }

  async function handleScan(rawCode?: string) {
    window.clearTimeout(scanTimerRef.current);
    const code = normalizeScannedCode(rawCode ?? inputRef.current?.value ?? scanValue);
    const kind = detectScanCodeKind(code);

    if (!code || submitting || successMessage) {
      return;
    }

    if (step === 'location') {
      if (kind === 'location') {
        setLocCode(code);
        clearScanInput();
        notifySuccess(`库位 ${code}`);
        return;
      }
      if (kind === 'offcut') {
        notifyError('先扫库位，再扫料头');
        clearScanInput();
        return;
      }
      showUnsupportedCodeError(kind);
      clearScanInput();
      return;
    }

    if (step === 'offcut') {
      if (kind === 'offcut') {
        if (pendingItems.some((item) => item.ocsf === code)) {
          notifyError('这个料头已经在待提交列表里了');
          clearScanInput();
          return;
        }

        setDraftOcsf(code);
        clearScanInput();
        notifySuccess(`料头 ${code}`);
        return;
      }
      if (kind === 'location') {
        notifyError('库位已记录，如需更换请点重扫库位');
        clearScanInput();
        return;
      }
      showUnsupportedCodeError(kind);
      clearScanInput();
    }
  }

  function scheduleScan(rawCode: string) {
    if (submitting || successMessage || step === 'details') {
      return;
    }

    setScanValue(rawCode);
    window.clearTimeout(scanTimerRef.current);
    scanTimerRef.current = window.setTimeout(() => {
      void handleScan(inputRef.current?.value ?? rawCode);
    }, AUTO_SCAN_DELAY_MS);
  }

  function handleAddPendingItem() {
    const parsedWeight = Number(draftWeight);
    const parsedLength = Number(draftLength);

    if (!draftOcsf) {
      notifyError('先扫料头 QR');
      return;
    }

    if (!Number.isFinite(parsedWeight) || parsedWeight <= 0 || parsedWeight > 1000) {
      notifyError('重量要大于 0 小于 1000kg');
      return;
    }

    if (!Number.isFinite(parsedLength) || parsedLength <= 0) {
      notifyError('长度必须大于 0');
      return;
    }

    setPendingItems((prev) => prev.concat({
      id: Date.now() + prev.length,
      length: parsedLength,
      note: undefined,
      ocsf: draftOcsf,
      source_no: draftSourceNo.trim() || undefined,
      source_type: draftSourceType,
      weight: parsedWeight,
    }));
    notifySuccess(`已加入 ${draftOcsf}`);
    clearCurrentDraft();
  }

  function handleRemovePendingItem(id: number) {
    setPendingItems((prev) => prev.filter((item) => item.id !== id));
  }

  function buildLegacyInboundNote(item: ScanInboundItemPayload) {
    const parts = [`长度=${item.length}mm`, `来源=${item.source_type || '未填'}`];

    if (item.source_no) {
      parts.push(`来源单号=${item.source_no}`);
    }

    if (item.note) {
      parts.push(item.note);
    }

    return parts.join('；');
  }

  function shouldFallbackToLegacyInbound(error: any) {
    const code = String(
      error?.code
      ?? error?.data?.code
      ?? error?.response?.data?.code
      ?? '',
    );

    if (code && KNOWN_INBOUND_ERROR_CODES.has(code)) {
      return false;
    }

    const message = String(
      error?.msg
      ?? error?.message
      ?? error?.data?.msg
      ?? error?.response?.data?.msg
      ?? '',
    ).toLowerCase();

    return (
      !code
      || message.indexOf('items') >= 0
      || message.indexOf('ocsf') >= 0
      || message.indexOf('weight') >= 0
      || message.indexOf('json') >= 0
      || message.indexOf('schema') >= 0
      || message.indexOf('unmarshal') >= 0
      || message.indexOf('unknown field') >= 0
      || message.indexOf('required') >= 0
    );
  }

  async function submitInboundCompat() {
    const items = pendingItems.map(({ id: _id, ...item }) => item);

    try {
      await scanOffcutInboundBatch({
        items,
        loc_code: locCode,
      });
      return;
    } catch (error) {
      if (!shouldFallbackToLegacyInbound(error)) {
        throw error;
      }
    }

    for (let index = 0; index < items.length; index += 1) {
      const item = items[index];

      await scanOffcutInbound({
        loc_code: locCode,
        note: buildLegacyInboundNote(item),
        ocsf: item.ocsf,
        weight: item.weight,
      });
    }
  }

  async function handleSubmitAll() {
    if (!locCode) {
      notifyError('先扫库位');
      return;
    }

    if (pendingItems.length === 0) {
      notifyError('先加入至少一个料头');
      return;
    }

    setSubmitting(true);
    try {
      await submitInboundCompat();

      const message = `已入库 ${pendingItems.length} 件到 ${locCode}`;
      setSuccessMessage(message);
      notifySuccess(message);
      scheduleResetAfterSubmit();
    } catch (error) {
      notifyError(getPdaErrorMessage(error, '入库失败'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <PdaPage
      actionLabel="重置"
      onAction={resetFlow}
      onBack={() => navigateTo('/home')}
      pageClassName="pre-inbound-page"
      subtitle="库位码 → 多个料头码 → 每件重量长度"
      title="料头入库"
    >
      <section className="pre-progress-card">
        <div className="pre-progress-title">当前进度</div>
        <div className="pre-step-track">
          {progressSteps.map((item) => {
            const StepIcon = item.icon;
            const className = [
              'pre-step',
              item.complete ? 'pre-step--complete' : '',
              item.current ? 'pre-step--current' : '',
              item.complete ? 'pre-step--line-complete' : '',
            ].filter(Boolean).join(' ');

            return (
              <div className={className} key={item.label}>
                <div className="pre-step__line" />
                <div className="pre-step__icon">
                  <StepIcon aria-hidden="true" size={15} strokeWidth={2.4} />
                </div>
                <div className="pre-step__label">{item.label}</div>
              </div>
            );
          })}
        </div>
        <div className="pre-progress-count">
          <div className="pre-progress-count__label">待提交</div>
          <div className="pre-progress-count__value">{pendingItems.length || '--'} 件</div>
          <div className="pre-progress-count__sub">
            {pendingItems.length ? `${formatWeight(totalPendingWeight)}kg` : '--'}
          </div>
        </div>
      </section>

      <section className="pre-info-card">
        <div className="pre-info-grid">
          <div className="pre-info-cell">
            <div className="pre-label">操作人</div>
            <div className="pre-value">
              {operatorDisplay}{operator.emp_no ? ` · ${operator.emp_no}` : ''}
            </div>
          </div>
          <div className="pre-info-cell">
            <div className="pre-label">当前库位</div>
            <div className="pre-value">{locCode || '等待扫码'}</div>
          </div>
        </div>
        <button className="pre-order-card" disabled={!draftOcsf} type="button">
          <div>
            <div className="pre-label">当前料头</div>
            <div className="pre-order-title">{draftOcsf || '等待扫码'}</div>
            <div className="pre-order-sub">
              {step === 'details' ? `${draftWeight || '--'}kg / ${draftLength || '--'}mm` : '扫到料头后录入明细'}
            </div>
          </div>
          <span className="pre-chevron">›</span>
        </button>
      </section>

      {successMessage ? (
        <section className="panel success-panel">
          <div className="success-icon">✓</div>
          <div className="success-text">{successMessage}</div>
          <div className="panel-hint">2 秒后保留当前库位，回到“继续扫料头”。</div>
        </section>
      ) : step !== 'details' ? (
        <section className="pre-scan-card" key={`scan-step-${step}-${locCode}-${pendingItems.length}`}>
          <div className="panel-title">{step === 'location' ? '先扫目标库位' : '继续扫料头 QR'}</div>
          <div className="pre-scan-box">
            <ScanLine className="pre-scan-mark" aria-hidden="true" size={18} strokeWidth={2.2} />
            <input
              autoComplete="off"
              autoFocus
              autoCapitalize="off"
              autoCorrect="off"
              className="pre-scan-input"
              id="scan-inbound-code"
              inputMode="none"
              onInput={(event) => scheduleScan(event.currentTarget.value)}
              onKeyUp={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  void handleScan(event.currentTarget.value);
                }
              }}
              placeholder="扫描后按回车..."
              ref={inputRef}
              spellCheck={false}
            />
            <Keyboard className="pre-keyboard-mark" aria-hidden="true" size={18} strokeWidth={2.2} />
          </div>
          {locCode ? (
            <div className="button-row" style={{ marginTop: 8 }}>
              <button className="secondary-btn" onClick={handleRescanLocation} type="button">
                重扫库位
              </button>
            </div>
          ) : null}
        </section>
      ) : (
        <section className="pre-entry-card" key={`details-step-${draftOcsf || 'empty'}`}>
          <div className="pre-entry-head">
            <div>
              <div className="pre-entry-title">录入当前料头</div>
              <div className="pre-entry-sub">{draftOcsf} · 来源 {draftSourceType}</div>
            </div>
            <span className="pre-chevron">›</span>
          </div>

          <div className="pre-field-grid">
            <label className="pre-field-cell" htmlFor="scan-inbound-weight">
              <span>重量(kg)</span>
              <input
                id="scan-inbound-weight"
                inputMode="decimal"
                onChange={(event) => setDraftWeight(event.target.value)}
                placeholder="10.5"
                value={draftWeight}
              />
            </label>
            <label className="pre-field-cell" htmlFor="scan-inbound-length">
              <span>长度(mm)</span>
              <input
                id="scan-inbound-length"
                inputMode="decimal"
                onChange={(event) => setDraftLength(event.target.value)}
                placeholder="120"
                value={draftLength}
              />
            </label>
            <label className="pre-field-cell" htmlFor="scan-inbound-source-type">
              <span>来源类别</span>
              <select
                id="scan-inbound-source-type"
                onChange={(event) => setDraftSourceType(event.target.value as (typeof SOURCE_TYPE_OPTIONS)[number])}
                value={draftSourceType}
              >
                {SOURCE_TYPE_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <label className="pre-note-field" htmlFor="scan-inbound-source-no">
            <span>来源单号(可选)</span>
            <input
              id="scan-inbound-source-no"
              onChange={(event) => setDraftSourceNo(event.target.value)}
              placeholder="例如: MO-240421-001"
              value={draftSourceNo}
            />
            <span className="pre-note-edit" aria-hidden="true" />
          </label>

          <div className="button-row" style={{ padding: 9 }}>
            <button className="secondary-btn" onClick={clearCurrentDraft} type="button">
              取消当前
            </button>
            <button
              className="primary-btn"
              disabled={submitting || !draftWeight.trim() || !draftLength.trim()}
              onClick={handleAddPendingItem}
              type="button"
            >
              加入待提交
            </button>
          </div>
        </section>
      )}

      {pendingItems.length > 0 ? (
        <section className="pre-entry-card">
          <div className="pre-entry-head">
            <div>
              <div className="pre-entry-title">{`待提交料头(${pendingItems.length})`}</div>
              <div className="pre-entry-sub">累计 {formatWeight(totalPendingWeight)}kg</div>
            </div>
            <span className="pre-chevron">›</span>
          </div>
          <div className="pending-list" style={{ padding: 9 }}>
            {pendingItems.map((item) => (
              <div className="pending-item" key={item.id}>
                <div className="pending-item__top">
                  <div className="pending-item__code">{item.ocsf}</div>
                  <button className="text-btn" onClick={() => handleRemovePendingItem(item.id)} type="button">
                    ×
                  </button>
                </div>
                <div className="pending-item__meta">
                  {`重量 ${formatWeight(item.weight)}kg`}
                  {' · '}
                  {`长度 ${item.length}mm`}
                  {' · '}
                  {item.source_type || '未选来源'}
                  {item.source_no ? ` · 单号 ${item.source_no}` : ''}
                </div>
              </div>
            ))}
          </div>
          <div className="button-row" style={{ padding: 9, paddingTop: 0 }}>
            <button className="secondary-btn" disabled={submitting} onClick={clearCurrentBatch} type="button">
              清空本次
            </button>
            <button className="primary-btn" disabled={submitting} onClick={() => void handleSubmitAll()} type="button">
              {submitting ? '提交中...' : `提交全部（${pendingItems.length}）`}
            </button>
          </div>
        </section>
      ) : null}
    </PdaPage>
  );
}

export default ScanOffcutInbound;
